import * as geometryEngine from "@arcgis/core/geometry/geometryEngine";
import * as projection from "@arcgis/core/geometry/projection";
import SpatialReference from "@arcgis/core/geometry/SpatialReference";
import { KrookiePlot } from "../services/reducers/master-plan-modification";
import { LandPlot } from "../interfaces/card";
import { LayersDataTypes } from "../types/layers-data-types";
import { Layer } from "../interfaces/layer";
import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import { isEmptyGeometry } from "./is-empty-geometry";

export type PlotForSelfAndDuplicate = Pick<
  KrookiePlot,
  "newGeometry" | "objectId"
>;
export type PlotForIntersection = Pick<LandPlot, "geometry" | "objectId">;

export const hasDuplicatePoints = async (
  currentPlots: PlotForSelfAndDuplicate[],
): Promise<boolean> => {
  const allPoints = new Set<string>();

  for (const plot of currentPlots) {
    const polygon = plot.newGeometry;
    if (!polygon || !polygon.rings) continue;

    for (const ring of polygon.rings) {
      const seenInRing = new Set<string>();
      const points = ring.slice(0, -1);

      for (const [x, y] of points) {
        const key = `${x},${y}`;

        if (seenInRing.has(key)) {
          return true;
        }
        seenInRing.add(key);

        if (allPoints.has(key)) {
          return true;
        }
        allPoints.add(key);
      }
    }
  }

  return false;
};

export const hasSelfIntersections = async (
  currentPlots: PlotForSelfAndDuplicate[],
): Promise<boolean> => {
  for (const plot of currentPlots) {
    const polygon = plot.newGeometry;
    if (!polygon) continue;

    const isSimple = geometryEngine.isSimple(polygon);
    if (!isSimple) {
      return true;
    }
  }

  return false;
};

export const hasIntersections = async (
  currentPlots: PlotForSelfAndDuplicate[],
  allPlots: PlotForIntersection[],
  initialPlots?: LandPlot[],
): Promise<boolean> => {
  const initialObjectIds = new Set(initialPlots?.map((plot) => plot.objectId));

  for (const plot of currentPlots) {
    const newGeom = plot.newGeometry;
    if (!newGeom) continue;

    for (const existing of allPlots) {
      const existingGeom = existing.geometry;
      if (!existingGeom) continue;

      if (isEmptyGeometry(existing.geometry)) {
        continue;
      }

      if (initialObjectIds.has(existing.objectId)) {
        continue;
      }

      const isIntersecting = geometryEngine.intersects(newGeom, existingGeom);
      const isTouching = geometryEngine.touches(newGeom, existingGeom);

      if (isIntersecting && !isTouching) {
        return true;
      }
    }
  }

  return false;
};

export const hasIntersectionsWithInitialPlot = async (
  currentPlots: PlotForSelfAndDuplicate[],
  initialPlots?: LandPlot[],
): Promise<boolean> => {
  if (!initialPlots || initialPlots.length === 0) return false;

  for (const plot of currentPlots) {
    const newGeom = plot.newGeometry;
    if (!newGeom) continue;

    for (const initialPlot of initialPlots) {
      const initialGeom = initialPlot.geometry;
      if (!initialGeom) continue;

      const isIntersecting = geometryEngine.intersects(newGeom, initialGeom);
      const isTouching = geometryEngine.touches(newGeom, initialGeom);

      if (isIntersecting && !isTouching) {
        return false;
      }
    }
  }

  return true;
};

export const isOutsideCityBoundary = async (
  currentPlots: PlotForSelfAndDuplicate[],
  layers: Layer[],
  cityId: number,
): Promise<boolean> => {
  const filteredCityLayers = layers.filter(
    (layer) => layer.dataType === LayersDataTypes.CityBoundary && !!layer.url,
  );

  const featureLayers = filteredCityLayers.map(
    (layer) =>
      new FeatureLayer({
        url: layer.url!,
        outFields: ["*"],
      }),
  );

  const plotGeometry = currentPlots?.[0]?.newGeometry;
  if (!plotGeometry) {
    return false;
  }

  for (const layer of featureLayers) {
    await layer.load();

    const query = layer.createQuery();
    query.where = `estateEn = ${cityId}`;
    query.returnGeometry = true;

    const result = await layer.queryFeatures(query);
    const features = result.features.map((feature) => feature.geometry);

    if (!features.length) continue;

    const mergedCityFeatures = geometryEngine.union(features);

    if (geometryEngine.contains(mergedCityFeatures, plotGeometry)) return false;
  }

  return true;
};

// Returns the SerialNumber of the estate (city boundary) polygon that contains
// the plot's center point. Estates can have several polygons with different
// serials, so we match by the containing polygon — not by estate id.
export const getEstateSerialForPlot = async (
  plotGeometry: __esri.Polygon | null | undefined,
  layers: Layer[],
): Promise<string | null> => {
  if (!plotGeometry) return null;

  const center = plotGeometry.centroid;
  if (!center) return null;

  const estateLayers = layers.filter(
    (layer) => layer.dataType === LayersDataTypes.CityBoundary && !!layer.url,
  );

  for (const layer of estateLayers) {
    try {
      const featureLayer = new FeatureLayer({ url: layer.url!, outFields: ["*"] });
      await featureLayer.load();

      const query = featureLayer.createQuery();
      query.geometry = center;
      query.spatialRelationship = "intersects";
      query.outFields = ["SerialNumber"];
      query.returnGeometry = false;

      const result = await featureLayer.queryFeatures(query);
      const serial = result.features?.[0]?.attributes?.SerialNumber;
      if (serial != null && String(serial).trim() !== "") {
        return String(serial);
      }
    } catch (err) {
      console.error("Failed to query estate serial, skipping layer:", err);
    }
  }

  return null;
};

export const hasUnclosedRings = (
  currentPlots: PlotForSelfAndDuplicate[],
): boolean => {
  for (const plot of currentPlots) {
    const rings = plot.newGeometry?.rings;
    if (!rings) continue;

    for (const ring of rings) {
      if (ring.length < 4) {
        return true;
      }
      const first = ring[0];
      const last = ring[ring.length - 1];
      if (first[0] !== last[0] || first[1] !== last[1]) {
        return true;
      }
    }
  }
  return false;
};

// The QR is printed on an official document and scanned from outside the
// company network, so it must always point at the public production
// platform — not window.location.origin, which on staging is an internal
// address. Overridable per build via REACT_APP_PUBLIC_PLATFORM_URL.
// Exported so other Krookie QR/link builders (e.g. the plot-search link in
// master-plan-modification.ts) use the same production URL instead of each
// hardcoding window.location.origin separately — that's exactly what
// leaked a staging-internal URL into a QR code once before.
export const PUBLIC_PLATFORM_URL = (
  process.env.REACT_APP_PUBLIC_PLATFORM_URL ?? "https://explore.madayn.om"
).replace(/\/$/, "");

// A plot without a real fid yet (e.g. one created through this app, whose
// fid isn't written at creation time) reads back as the string "0" via
// getFid()'s `.toString()` — a non-empty string, so a plain `if (fid)`
// check treats it as present and builds a broken `fid=0` deep-link. "0" is
// never a real fid, so it's treated the same as missing.
export const hasValidFid = (fid: unknown): boolean => {
  const value = String(fid ?? "").trim();
  return value !== "" && value !== "0";
};

// Platform (Explore Madayn) link for the QR code on approved Krookie
// documents. When the plot's `fid` (its id in the public Explore plots
// dataset) is already known, the link opens that plot's own info card
// directly (same as "Show on map" + "Show details" together) via the
// existing fid/fromQr deep-link handling in MainMapPage. A brand-new plot
// doesn't have a `fid` yet at the moment the approval PDF is generated —
// it isn't added to ArcGIS until just after — so without one this falls
// back to a plain map link centered on the plot's geometry, which is all
// that's possible at that point. Without a fid there's also no specific
// plot for MainMapPage to select, so nothing tells it which layers to
// show — the map otherwise lands on its default layers, which don't
// include the plots layer, leaving the destination looking empty.
// Passing the plots layer's own id(s) via `lId` (the same param
// useSyncMapSearchParams reads on load) makes it visible regardless.
export const getPlotLocationLink = async (
  plotGeometry: __esri.Polygon | null | undefined,
  fid?: string | null,
  plotsLayerIds?: number[],
): Promise<string | null> => {
  if (!plotGeometry) return null;
  const center = plotGeometry.centroid;
  if (!center) return null;
  try {
    await projection.load();
    const projected = projection.project(
      center,
      new SpatialReference({ wkid: 4326 }),
    ) as __esri.Point | null;
    if (!projected) return null;
    const lIdParam =
      plotsLayerIds && plotsLayerIds.length > 0
        ? `&lId=${plotsLayerIds.join(",")}`
        : "";
    if (hasValidFid(fid)) {
      return `${PUBLIC_PLATFORM_URL}/search?fid=${encodeURIComponent(fid!)}&fromQr=1&ll=${projected.y.toFixed(5)},${projected.x.toFixed(5)}&z=18${lIdParam}`;
    }
    return `${PUBLIC_PLATFORM_URL}/search?ll=${projected.y.toFixed(5)},${projected.x.toFixed(5)}&z=18${lIdParam}`;
  } catch (err) {
    console.error("Failed to build plot location link:", err);
    return null;
  }
};
